import { API } from "../../utils/api";
import { useState, useEffect, useRef } from "react";
import { Box, TextField, Button, Typography, Paper, List, ListItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { Container } from "@mui/material";
import { useFormik } from "formik";
import { H1 } from "../../components/reusable/Heading";
import { Spinner } from "../../components/reusable/Spinner";
import SnackbarAlert from "../../components/reusable/SnackbarAlert/index";
import logo from "../../images/connect-logo.png";
import { useSearchParams } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { withStyles } from "@material-ui/core/styles";
import EmailIcon from "@mui/icons-material/Email";
import PasswordIcon from "@mui/icons-material/Password";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { InputAdornment } from "@material-ui/core";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import * as yup from "yup";
import t from "../../i18n";

const CssTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      textDecoration: "none",
      color: "inherit",
    },
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#91BE23",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#91BE23",
      },
    },
  },
})(TextField);

const Powerforce = () => {
  const [searchParams] = useSearchParams();
  const businessIdParam = searchParams.get("b");
  const sg = searchParams.get("sg");

  const [alert, setAlert] = useState(null);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [businessLocation, setBusinessLocation] = useState(null);
  const [companyId] = useState(597);
  const [segment, setSegment] = useState("");
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const addressContainerRef = useRef(null);
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);
  const [showBusinessList, setShowBusinessList] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [countryCode, setCountryCode] = useState("gb");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selected, setSelected] = useState("");
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualEntryData, setManualEntryData] = useState({
    businessName: "",
    street: "",
    number: "",
    zip: "",
    city: "",
  });
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isValidSelection, setIsValidSelection] = useState(false);
  const [locationDenied, setLocationDenied] = useState(false);
  const isProgrammaticChange = useRef(false);
  const justClosedDialog = useRef(false);

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(t("enter_valid_email"))
      .required(t("email_required")),
    password: yup
      .string()
      .min(6, "Your password must be 6 characters or longer.")
      .required(t("password_required")),
    address: yup.string().required("Address is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      address: "",
      street: "",
    number: "",
      city: "",
    zip: "",
      businessName: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      await handleRegistration(values);
    },
  });

  const parseAddress = (addressData, businessName = "") => {
    const displayAddress = businessName 
      ? `${businessName}, ${addressData.street || ""} ${addressData.number || ""}, ${addressData.city || ""} ${addressData.zip || ""}`
      : `${addressData.street || ""} ${addressData.number || ""}, ${addressData.city || ""} ${addressData.zip || ""}`;

    isProgrammaticChange.current = true;
    formik.setValues({
      ...formik.values,
      street: addressData.street || "",
      number: addressData.number || "",
      city: addressData.city || "",
      zip: addressData.zip || "",
      address: displayAddress,
      businessName: businessName,
    });
    // Reset the flag after a short delay to ensure the change event is processed
    setTimeout(() => {
      isProgrammaticChange.current = false;
    }, 0);
  };

  // Load existing business location if businessIdParam exists
  useEffect(() => {
    const getLocationData = async () => {
      if (businessIdParam) {
        const locationData = await API.loadLocationForBusiness(businessIdParam);
        if (locationData.data) {
          setBusinessLocation(locationData.data);
          isProgrammaticChange.current = true;
          formik.setValues({
            ...formik.values,
            street: locationData.data.street || "",
            number: locationData.data.number || "",
            city: locationData.data.city || "",
            zip: locationData.data.zip || "",
            address: `${locationData.data.street} ${locationData.data.number}, ${locationData.data.city} ${locationData.data.zip}`,
          });
          setTimeout(() => {
            isProgrammaticChange.current = false;
          }, 0);
          setIsValidSelection(true);
        }
      }
    };
    getLocationData();
  }, [businessIdParam]);

  // Automatically get location on page load
  useEffect(() => {
    // Only auto-load if there's no existing business location
    if (!businessIdParam && !currentLocation) {
      getCurrentLocation();
    }
  }, []); // Empty dependency array means this runs once on mount

  // Close business list when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showBusinessList &&
        addressContainerRef.current &&
        !addressContainerRef.current.contains(event.target)
      ) {
        setShowBusinessList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showBusinessList]);

  const handleClose = () => {
    setOpen(false);
  };

  const getCurrentLocation = () => {
    setIsSearchingLocation(true);
    setShowBusinessList(false);
    setNearbyBusinesses([]);

    if (!navigator.geolocation) {
      setAlert({
        variant: "error",
        message: "Geolocation is not supported by your browser.",
        open: true,
      });
      setOpen(true);
      setIsSearchingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ latitude, longitude });
        searchNearbyBusinesses(latitude, longitude);
      },
      (error) => {
        setLocationDenied(true);
        // setAlert({
        //   variant: "error",
        //   message: "Unable to get your location. Please enable location permissions.",
        //   open: true,
        // });
        setOpen(true);
        setIsSearchingLocation(false);
      }
    );
  };

  const searchNearbyBusinesses = async (latitude, longitude) => {
    try {
      const response = await API.searchNearbyPlaces(latitude, longitude, 1000);
      
      setIsSearchingLocation(false);
      
      if (response.status === 200) {
        setNearbyBusinesses(response.results || []);
        setShowBusinessList(true);
        
        // Set country code if returned
        if (response.country_code) {
          setCountryCode(response.country_code);
          setSelectedCountry(response.country_code);
        }
      } else {
        setAlert({
          variant: "error",
          message: "Error searching for nearby businesses.",
          open: true,
        });
        setOpen(true);
      }
    } catch (error) {
      setIsSearchingLocation(false);
      setAlert({
        variant: "error",
        message: "Error searching for nearby businesses.",
        open: true,
      });
      setOpen(true);
    }
  };

  const handleBusinessSelect = async (place) => {
    try {
      // Fetch detailed place information including postal code
      const details = await API.getPlaceDetails(place.place_id);
      
      if (details.status === 200 && details.address) {
        const addressData = {
          street: details.address.street || "",
          number: details.address.number || "",
          city: details.address.city || "",
          zip: details.address.zip || "",
        };

        parseAddress(addressData, details.name || place.name);
        setIsValidSelection(true);
        setShowBusinessList(false);
        setNearbyBusinesses([]);
      } else {
        // Fallback to existing parsing logic if place details fetch fails
        const businessName = place.name || "";
        const addressData = {
          street: "",
          number: "",
          city: place.vicinity ? place.vicinity.split(',')[1]?.trim() || "" : "",
          zip: place.zip_code || place.postal_code || "",
        };

        // If place has geometry, we can try to parse the vicinity
        // The vicinity usually has the format: "street number, city"
        if (place.vicinity) {
          const parts = place.vicinity.split(',');
          if (parts.length > 0) {
            const streetParts = parts[0].trim().split(' ');
            if (streetParts.length > 1) {
              // Last part might be the number
              const lastPart = streetParts[streetParts.length - 1];
              if (/^\d/.test(lastPart)) {
                addressData.number = lastPart;
                addressData.street = streetParts.slice(0, -1).join(' ');
              } else {
                addressData.street = parts[0].trim();
              }
            } else {
              addressData.street = parts[0].trim();
            }
          }
          
          // Try to extract city from vicinity if not already set
          if (!addressData.city && parts.length > 1) {
            addressData.city = parts[1].trim();
          }
        }

        parseAddress(addressData, businessName);
        setIsValidSelection(true);
        setShowBusinessList(false);
        setNearbyBusinesses([]);
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
      
      // Fallback to existing parsing logic if there's an error
      const businessName = place.name || "";
      const addressData = {
        street: "",
        number: "",
        city: place.vicinity ? place.vicinity.split(',')[1]?.trim() || "" : "",
        zip: place.zip_code || place.postal_code || "",
      };

      // If place has geometry, we can try to parse the vicinity
      // The vicinity usually has the format: "street number, city"
      if (place.vicinity) {
        const parts = place.vicinity.split(',');
        if (parts.length > 0) {
          const streetParts = parts[0].trim().split(' ');
          if (streetParts.length > 1) {
            // Last part might be the number
            const lastPart = streetParts[streetParts.length - 1];
            if (/^\d/.test(lastPart)) {
              addressData.number = lastPart;
              addressData.street = streetParts.slice(0, -1).join(' ');
            } else {
              addressData.street = parts[0].trim();
            }
          } else {
            addressData.street = parts[0].trim();
          }
        }
        
        // Try to extract city from vicinity if not already set
        if (!addressData.city && parts.length > 1) {
          addressData.city = parts[1].trim();
        }
      }

      parseAddress(addressData, businessName);
      setIsValidSelection(true);
      setShowBusinessList(false);
      setNearbyBusinesses([]);
    }
  };

  const handleManualEntryOpen = async () => {
    setShowBusinessList(false);
    
    // If we have current location, reverse geocode to get address
    if (currentLocation) {
      try {
        const response = await API.geocodeLocation(currentLocation.latitude, currentLocation.longitude);
        
        if (response.status === 200 && response.address) {
          // Pre-fill the manual entry data with geocoded address
          setManualEntryData({
            businessName: "",
            street: response.address.street || "",
            number: response.address.number || "",
            zip: response.address.zip || "",
            city: response.address.city || "",
          });
        } else {
          // If geocoding fails, just open empty popup
          setManualEntryData({
            businessName: "",
            street: "",
            number: "",
            zip: "",
            city: "",
          });
        }
      } catch (error) {
        // If geocoding fails, just open empty popup
        setManualEntryData({
          businessName: "",
          street: "",
          number: "",
          zip: "",
          city: "",
        });
      }
    } else {
      // No location available, just open empty popup
      setManualEntryData({
        businessName: "",
        street: "",
        number: "",
        zip: "",
        city: "",
      });
    }
    
    setShowManualEntry(true);
  };

  const handleManualEntryClose = (event, reason) => {
    console.log('Closing manual entry dialog', reason);
    setShowManualEntry(false);
    setShowBusinessList(false);
    setManualEntryData({
      businessName: "",
      street: "",
      number: "",
      zip: "",
      city: "",
    });
    
    // Prevent immediate reopening when focus returns to the address field
    justClosedDialog.current = true;
    setTimeout(() => {
      justClosedDialog.current = false;
    }, 300);
  };

  const handleManualEntryChange = (field, value) => {
    setManualEntryData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleManualEntrySubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Manual entry submit clicked');
    
    // Validate that all fields are filled
    if (!manualEntryData.businessName || !manualEntryData.street || !manualEntryData.city || !manualEntryData.zip) {
      setAlert({
        variant: "error",
        message: "Please fill in all required fields.",
        open: true,
      });
      setOpen(true);
      return;
    }

    // Set the form values
    const displayAddress = `${manualEntryData.businessName}, ${manualEntryData.street} ${manualEntryData.number}, ${manualEntryData.city} ${manualEntryData.zip}`;
    
    isProgrammaticChange.current = true;
    formik.setValues({
      ...formik.values,
      street: manualEntryData.street,
      number: manualEntryData.number,
      city: manualEntryData.city,
      zip: manualEntryData.zip,
      address: displayAddress,
      businessName: manualEntryData.businessName,
    });
    setTimeout(() => {
      isProgrammaticChange.current = false;
    }, 0);

    setIsValidSelection(true);
    
    console.log('Closing dialog after submit');
    // Close the dialog and reset the form
    setShowManualEntry(false);
    setShowBusinessList(false);
    setManualEntryData({
      businessName: "",
      street: "",
      number: "",
      zip: "",
      city: "",
    });
    
    // Prevent immediate reopening when focus returns to the address field
    justClosedDialog.current = true;
    setTimeout(() => {
      justClosedDialog.current = false;
    }, 300);
  };

  const displayAlert = (alert) => (
      <SnackbarAlert
        handleClose={handleClose}
        display={open}
        variant={alert.variant}
        content={alert.message}
    />
  );

  const renderSegmentsSelect = () => {
    const selectOptions = [
      "snack_bar",
      "takeaway",
      "lunch_room",
      "sports_venue",
      "gas_station",
      "neighborhood_store",
      "restaurant",
      "eatery",
      "bar_or_cafe",
      "other",
    ];
    const handleChange = (event) => {
      setSegment(event.target.value);
      setSelected(event.target.value);
    };

    return (
      <Box sx={{ minWidth: 120 }}>
        <FormControl fullWidth>
          <InputLabel
            sx={{
              color: "grey",
              "&.Mui-focused": {
                color: "black",
              },
              marginBottom: "15px",
            }}
            id="segment-select-label"
          >
            {t("select_segment")}
          </InputLabel>
          <Select
            labelId="segment-select-label"
            id="segment-select"
            label={t("select_segment")}
            onChange={handleChange}
            value={selected}
            variant="outlined"
            sx={{
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#91BE23",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#91BE23",
              },
              marginBottom: "20px",
            }}
          >
            {selectOptions.map((selectOption) => (
              <MenuItem key={selectOption} value={selectOption}>
                {t(selectOption)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const handleRegistration = async (values) => {
    setIsLoading(true);

    try {
      // Validate required fields
      if (!values.email || !values.password || !values.address) {
        setAlert({
          variant: "error",
          message: "Please fill in all required fields.",
          open: true,
        });
        setOpen(true);
        setIsLoading(false);
        return;
      }

      if (!isValidSelection || !values.street || !values.city) {
        setAlert({
          variant: "error",
          message: "Please select a valid address from the suggestions or add your business manually.",
          open: true,
        });
        setOpen(true);
        setIsLoading(false);
        return;
      }

      // Use business name if available (from Google Places), otherwise use address
      const businessName = values.businessName || values.address;

      // Prepare registration data
      const registrationData = {
        spotter: {
          email: values.email,
          password: values.password,
          first_name: "",
          last_name: "",
          country_code:"gb",
          terms_accepted: true,
        },
        business: {
          name: businessName,
          business_id: businessIdParam,
          companyId: companyId,
          location: {
            street: values.street,
            number: values.number,
            zip: values.zip,
            city: values.city,
          },
          skip_location: businessLocation ? true : false,
        },
      };

      // Add segment_group if provided
      if (sg) {
        registrationData.business.segment_group = sg;
      } else if (segment) {
        registrationData.business.segment = segment;
      }

      // Call the new connect_signup endpoint
      const response = await API.connectSignup(registrationData);

      if (response.status === 200) {
        // Success - show success message
        setAlert({
          variant: "success",
          message: t("thank_you_for_registering") || "Registration successful!",
          open: true,
        });
        setOpen(true);

        // Save password to local storage temporarily
        localStorage.setItem("p", values.password);

        // Redirect to app after a short delay
        setTimeout(() => {
          localStorage.removeItem("p");
          window.location.href = "/app_url";
        }, 1500);
      } else if (response.status === 400) {
        // Handle specific error messages
        const errorMessage = response.errors || "Registration failed. Please try again.";
        
        setAlert({
          variant: "error",
          message: typeof errorMessage === 'string' 
            ? errorMessage 
            : errorMessage.join(', '),
          open: true,
        });
        setOpen(true);
      } else {
        setAlert({
          variant: "error",
          message: "An error occurred during registration. Please try again.",
          open: true,
        });
        setOpen(true);
      }
    } catch (error) {
      console.error("Registration failed:", error);
      setAlert({
        variant: "error",
        message: "An error occurred during registration. Please try again.",
        open: true,
      });
      setOpen(true);
    }

    setIsLoading(false);
  };

  return (
    <div>
      <div style={{ marginTop: "10%" }}>
        {/* Connect logo */}
        <img
          style={{ display: "block", margin: "70px auto" }}
          src={logo}
          alt="Connect Logo"
        />

        <Container>
          <H1>{t("registration")}</H1>
          <Box m={4}></Box>

          <form onSubmit={formik.handleSubmit} noValidate>
                        {/* Address Field with Google Places Autocomplete */}
                        <Box ref={addressContainerRef} style={{ position: "relative" }}>
              <CssTextField
                fullWidth
                variant="outlined"
                margin="normal"
                required
                id="address"
                name="address"
                label="Address"
                autoComplete="off"
                inputRef={addressInputRef}
                value={formik.values.address}
                onChange={(e) => {
                  formik.handleChange(e);
                  // Reset validation flag when user manually edits (but not on programmatic changes)
                  if (isValidSelection && !isProgrammaticChange.current) {
                    setIsValidSelection(false);
                  }
                }}
                onFocus={() => {
                  if (!isSearchingLocation) {
                    // If location was denied and we didn't just close the dialog, show manual entry
                    if (locationDenied && !justClosedDialog.current) {
                      handleManualEntryOpen();
                      return;
                    }
                    if (nearbyBusinesses.length > 0 || currentLocation) {
                      // If we already have businesses or have searched before, show the list
                      setShowBusinessList(true);
                    }
                    if (nearbyBusinesses.length === 0 && !currentLocation && !locationDenied) {
                      // If no businesses and haven't searched yet, get location and search
                      getCurrentLocation();
                    }
                  }
                }}
                onClick={() => {
                  if (!isSearchingLocation) {
                    // If location was denied and we didn't just close the dialog, show manual entry
                    if (locationDenied && !justClosedDialog.current) {
                      handleManualEntryOpen();
                      return;
                    }
                    if (nearbyBusinesses.length > 0 || currentLocation) {
                      // If we already have businesses or have searched before, show the list
                      setShowBusinessList(true);
                    }
                  }
                }}
                error={Boolean(formik.errors.address)}
                helperText={formik.errors.address}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
      <Button
                        size="small"
                        onClick={locationDenied ? handleManualEntryOpen : getCurrentLocation}
                        disabled={isSearchingLocation}
                        style={{ minWidth: "auto", padding: "4px 8px" }}
                      >
                        {isSearchingLocation ? (
                          <Spinner size={20} />
                        ) : (
                          <MyLocationIcon />
                        )}
      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              
              {/* Nearby Businesses List */}
              {showBusinessList && nearbyBusinesses.length > 0 && (
                <Paper
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    maxHeight: "300px",
                    overflow: "auto",
                    marginTop: "4px",
                  }}
                >
                  <List dense>
                    <ListItem style={{ justifyContent: "flex-end", paddingRight: "16px" }}>
                      <Button
                        onClick={handleManualEntryOpen}
                        variant="contained"
                        size="small"
                        style={{ 
                          backgroundColor: "rgb(23, 44, 83)",
                          color: "white",
                          textTransform: "none"
                        }}
                      >
                        My business is not in the list
                      </Button>
                    </ListItem>
                    {nearbyBusinesses.map((business, index) => {
                      // Format address with zip code if available
                      const addressText = business.vicinity || business.formatted_address || '';
                      const zipCode = business.zip_code || business.postal_code || '';
                      const displayAddress = zipCode ? `${addressText}, ${zipCode}` : addressText;
                      
                      return (
                        <ListItem
                          key={business.place_id || index}
                          button
                          onClick={() => handleBusinessSelect(business)}
                          style={{ cursor: "pointer" }}
                        >
                          <ListItemText
                            primary={business.name}
                            secondary={displayAddress}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              )}
              
              {/* No Results - Show Manual Entry Option */}
              {showBusinessList && nearbyBusinesses.length === 0 && !isSearchingLocation && (
                <Paper
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    padding: "16px",
                    marginTop: "4px",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    No businesses found nearby.
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{ 
                      color: "rgb(23, 44, 83)", 
                      cursor: "pointer",
                      textDecoration: "underline",
                      marginTop: "8px"
                    }}
                    onClick={handleManualEntryOpen}
                  >
                    Business not listed? Click here to add it manually.
                  </Typography>
                </Paper>
              )}
      </Box>
            {/* Email Field */}
            <CssTextField
              fullWidth
              variant="outlined"
              margin="normal"
              required
              id="email"
              name="email"
              label={t("email")}
              autoComplete="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.email)}
              helperText={formik.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Field */}
            <CssTextField
              fullWidth
              variant="outlined"
              margin="normal"
              required
              type="password"
              id="password"
              name="password"
              label={t("password")}
              autoComplete="new-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={Boolean(formik.errors.password)}
              helperText={formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PasswordIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Segment Select Field */}
        <Box m={2}></Box>

            {renderSegmentsSelect()}

        <Box m={2}></Box>

              {isLoading && <Spinner size={48}></Spinner>}

              <Button
              disabled={isLoading}
              style={{ 
                float: "right", 
                marginTop: "20px",
                backgroundColor: "rgb(23, 44, 83)",
                color: "white"
              }}
                type="submit"
                variant="contained"
              >
              {t("registration")}
              </Button>
          </form>

          {alert && displayAlert(alert)}
      </Container>

        {/* Manual Entry Dialog - Moved outside form to prevent event propagation issues */}
        <Dialog 
          open={showManualEntry} 
          onClose={handleManualEntryClose}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={false}
        >
          <DialogTitle>Add Business</DialogTitle>
          <DialogContent>
            <Box mt={2}>
              <CssTextField
                fullWidth
                variant="outlined"
                margin="normal"
                required
                label="Business Name"
                value={manualEntryData.businessName}
                onChange={(e) => handleManualEntryChange("businessName", e.target.value)}
              />
              <CssTextField
                fullWidth
                variant="outlined"
                margin="normal"
                required
                label="Street"
                value={manualEntryData.street}
                onChange={(e) => handleManualEntryChange("street", e.target.value)}
              />
              <CssTextField
                fullWidth
                variant="outlined"
                margin="normal"
                label="Number"
                value={manualEntryData.number}
                onChange={(e) => handleManualEntryChange("number", e.target.value)}
              />
              <CssTextField
                fullWidth
                variant="outlined"
                margin="normal"
                required
                label="ZIP Code"
                value={manualEntryData.zip}
                onChange={(e) => handleManualEntryChange("zip", e.target.value)}
              />
              <CssTextField
                fullWidth
                variant="outlined"
                margin="normal"
                required
                label="City"
                value={manualEntryData.city}
                onChange={(e) => handleManualEntryChange("city", e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Cancel button clicked');
                handleManualEntryClose();
              }} 
              color="default"
              type="button"
            >
              Cancel
            </Button>
            <Button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Add Business button clicked');
                handleManualEntrySubmit(e);
              }} 
              variant="contained"
              type="button"
              style={{
                backgroundColor: "rgb(23, 44, 83)",
                color: "white"
              }}
            >
              Add Business
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default Powerforce;
